// Staff end-of-day check-in: how the night went, what to flag, then the venue's own questions.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { motion } from 'motion/react';
import { Loader2, CheckCircle2, Pencil } from 'lucide-react';
import { supabase } from '@/src/lib/supabase';
import { useAppColors } from '@/src/lib/theme';
import { completeLinkedTasks } from '@/src/lib/taskLinks';
import { DEFAULT_EOD_QUESTIONS, type EodQuestion } from '@/src/hooks/useEodReports';
import {
  CHECKIN_FLAGS, RATINGS, checkinToAnswers, emptyCheckin, flagByKey, longDay, parseCheckin, timeOf, tradingDate, type Checkin,
} from '@/src/lib/checkin';
import { BackButton, Chip, Panel, useTone } from './eod-ui';

interface EodPageProps {
  venueId: string;
  userId: string;
  userRole?: string;
  userName?: string;
  tz?: string | null;
  /** Managers looking from the hub; staff always get tonight. */
  onBack?: () => void;
}

export default function EodPage({ venueId, userId, userRole = 'staff', userName = '', tz = null, onBack }: EodPageProps) {
  const c = useAppColors();
  const tone = useTone();
  const date = useMemo(() => tradingDate(tz), [tz]);
  const role = userRole === 'chef' || userRole === 'bartender' ? userRole : 'staff';

  const [questions, setQuestions] = useState<EodQuestion[]>([]);
  const [existing, setExisting] = useState<{ id: string; submittedAt: string; answers: Record<string, unknown> } | null>(null);
  const [form, setForm] = useState<Checkin>(emptyCheckin());
  const [allGood, setAllGood] = useState(false);
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const [{ data: v }, { data: rows }] = await Promise.all([
      supabase.from('venues').select('eod_questions').eq('id', venueId).maybeSingle(),
      supabase.from('eod_reports').select('id, submitted_at, answers').eq('venue_id', venueId).eq('user_id', userId).eq('report_date', date)
        .order('submitted_at', { ascending: false }).limit(1),
    ]);
    const saved = (v?.eod_questions as Record<string, EodQuestion[]> | null) ?? {};
    // The venue's own questions; the built-in defaults skip complaints (the flags cover those).
    setQuestions(saved[role]?.length ? saved[role] : (DEFAULT_EOD_QUESTIONS[role] ?? DEFAULT_EOD_QUESTIONS.staff).filter((q) => q.key !== 'guest_complaints'));
    const row = (rows ?? [])[0] as { id: string; submitted_at: string; answers: Record<string, unknown> | null } | undefined;
    if (row) {
      setExisting({ id: row.id, submittedAt: row.submitted_at, answers: row.answers ?? {} });
      const p = parseCheckin(row.answers);
      setForm(p);
      setAllGood(!!p.rating && p.flags.length === 0);
    } else {
      setExisting(null);
      setForm(emptyCheckin());
    }
    setLoading(false);
  }, [venueId, userId, date, role]);

  useEffect(() => { void load(); }, [load]);

  const toggleFlag = (k: string) => {
    setAllGood(false);
    setForm((f) => ({ ...f, flags: f.flags.includes(k) ? f.flags.filter((x) => x !== k) : [...f.flags, k] }));
  };

  const send = async () => {
    if (!form.rating) { setError('Pick how the night went first.'); return; }
    setSaving(true); setError(null);
    const answers = checkinToAnswers(form);
    let err: string | null = null;
    if (existing) {
      const { error: e } = await supabase.from('eod_reports').update({ answers, updated_at: new Date().toISOString(), translations: {} }).eq('id', existing.id);
      if (e) { // older database (no updated_at / translations columns, or no permission on them): save the answers alone
        const r = await supabase.from('eod_reports').update({ answers }).eq('id', existing.id);
        err = r.error?.message ?? null;
      } else err = null;
    } else {
      const { error: e } = await supabase.from('eod_reports').insert({ venue_id: venueId, user_id: userId, report_date: date, answers });
      err = e?.message ?? null;
      if (!err) await completeLinkedTasks(userId, { type: 'eod_report' });
    }
    setSaving(false);
    if (err) { setError(existing ? `Couldn't save your changes: ${err}` : err); return; }
    setEditing(false);
    await load();
  };

  if (loading) return <div className="flex items-center justify-center py-24"><Loader2 size={20} className="animate-spin" style={{ color: c.textFaint }} /></div>;

  const first = (userName || '').split(' ')[0];
  const showForm = !existing || editing;

  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}
      className="px-4 sm:px-8 py-6 max-w-xl mx-auto w-full pb-32">
      <div className="flex items-start gap-3 mb-6">
        {onBack && <BackButton onClick={onBack} label="Back to tonight" />}
        <div className="min-w-0">
          <p className="text-[13px]" style={{ color: c.textDim }}>{longDay(date)}</p>
          <h1 className="text-2xl font-bold tracking-tight" style={{ color: c.text }}>
            {showForm ? `How was your shift${first ? `, ${first}` : ''}?` : 'Check-in sent'}
          </h1>
          {showForm && <p className="text-[13px] mt-0.5" style={{ color: c.textDim }}>Takes about a minute. Only managers see this.</p>}
        </div>
      </div>

      {!showForm && existing ? (
        <div className="space-y-4">
          <Panel className="p-5">
            <div className="flex items-center gap-3">
              <CheckCircle2 size={22} style={{ color: c.accentSoft }} />
              <p className="text-sm" style={{ color: c.text }}>Sent at <b>{timeOf(existing.submittedAt, tz)}</b>. Thanks{first ? `, ${first}` : ''}.</p>
            </div>
            <div className="flex flex-wrap gap-2 mt-4">
              {form.rating && <Chip tone="grey">Night: {RATINGS.find((r) => r.key === form.rating)?.label}</Chip>}
              {form.flags.map((f) => <Chip key={f} tone={flagByKey(f)?.tone ?? 'grey'}>{flagByKey(f)?.short ?? f}</Chip>)}
              {form.flags.length === 0 && <Chip tone="grey">Nothing to flag</Chip>}
            </div>
            <div className="mt-4 space-y-3">
              {form.flags.filter((f) => form.flagNotes[f]).map((f) => (
                <div key={f}><p className="text-[11px] font-bold uppercase tracking-widest" style={{ color: c.textDim }}>{flagByKey(f)?.label}</p><p className="text-sm mt-0.5" style={{ color: c.text }}>{form.flagNotes[f]}</p></div>
              ))}
              {questions.filter((q) => form.questions[q.key]?.trim()).map((q) => (
                <div key={q.key}><p className="text-[11px] font-bold uppercase tracking-widest" style={{ color: c.textDim }}>{q.label}</p><p className="text-sm mt-0.5 whitespace-pre-wrap" style={{ color: c.text }}>{form.questions[q.key]}</p></div>
              ))}
              {form.note && <div><p className="text-[11px] font-bold uppercase tracking-widest" style={{ color: c.textDim }}>Anything else</p><p className="text-sm mt-0.5 whitespace-pre-wrap" style={{ color: c.text }}>{form.note}</p></div>}
            </div>
          </Panel>
          <button onClick={() => setEditing(true)} className="w-full h-12 rounded-xl text-sm font-semibold flex items-center justify-center gap-2"
            style={{ background: c.bgElevated, border: `1px solid ${c.border}`, color: c.text }}>
            <Pencil size={15} /> Change my check-in
          </button>
        </div>
      ) : (
        <div className="space-y-6">
          <fieldset>
            <legend className="text-sm font-bold mb-2.5" style={{ color: c.text }}>The night overall</legend>
            <div className="grid grid-cols-4 gap-2">
              {RATINGS.map((r) => {
                const on = form.rating === r.key;
                return (
                  <button key={r.key} aria-pressed={on} onClick={() => { setForm((f) => ({ ...f, rating: r.key })); setError(null); }}
                    className="h-12 rounded-xl text-sm transition-colors"
                    style={on ? { background: c.accentBg, color: c.accentSoft, border: `2px solid ${c.accent}`, fontWeight: 700 } : { background: c.bgElevated, color: c.text, border: `1px solid ${c.border}`, fontWeight: 600 }}>
                    {r.label}
                  </button>
                );
              })}
            </div>
          </fieldset>

          <fieldset>
            <legend className="text-sm font-bold mb-2.5" style={{ color: c.text }}>Anything to flag? Tap all that apply</legend>
            <div className="flex flex-wrap gap-2">
              {CHECKIN_FLAGS.map((f) => {
                const on = form.flags.includes(f.key);
                const t = tone(f.tone);
                return (
                  <button key={f.key} aria-pressed={on} onClick={() => toggleFlag(f.key)}
                    className="h-11 px-3.5 rounded-full text-sm transition-colors"
                    style={on ? { background: t.background, color: t.color, border: `2px solid ${t.borderColor}`, fontWeight: 700 } : { background: c.bgElevated, color: c.text, border: `1px solid ${c.border}`, fontWeight: 600 }}>
                    {f.label}
                  </button>
                );
              })}
              <button aria-pressed={allGood} onClick={() => { setAllGood(true); setForm((f) => ({ ...f, flags: [] })); }}
                className="h-11 px-3.5 rounded-full text-sm transition-colors"
                style={allGood ? { background: c.accentBg, color: c.accentSoft, border: `2px solid ${c.accent}`, fontWeight: 700 } : { background: c.bgElevated, color: c.text, border: `1px solid ${c.border}`, fontWeight: 600 }}>
                Nothing, all good
              </button>
            </div>
          </fieldset>

          {form.flags.map((k) => {
            const f = flagByKey(k);
            if (!f) return null;
            return (
              <label key={k} className="block rounded-2xl p-3.5" style={{ background: c.bgElevated, border: `1px solid ${c.border}` }}>
                <span className="block text-[13px] font-bold mb-1.5" style={{ color: tone(f.tone).color }}>{f.label}: {f.prompt.charAt(0).toLowerCase() + f.prompt.slice(1)}</span>
                <textarea rows={2} value={form.flagNotes[k] ?? ''} onChange={(e) => { const t = e.target.value; setForm((x) => ({ ...x, flagNotes: { ...x.flagNotes, [k]: t } })); }}
                  className="w-full bg-transparent outline-none resize-none text-[15px] leading-relaxed" style={{ color: c.text }} />
              </label>
            );
          })}

          {questions.length > 0 && (
            <div>
              <p className="text-sm font-bold" style={{ color: c.text }}>A few questions from your venue</p>
              <p className="text-[12px] mb-2.5" style={{ color: c.textDim }}>Optional. Answer what's relevant to your shift.</p>
              <div className="rounded-2xl overflow-hidden" style={{ background: c.bgElevated, border: `1px solid ${c.border}` }}>
                {questions.map((q, i) => (
                  <label key={q.key} className="block px-3.5 py-3" style={{ borderTop: i ? `1px solid ${c.border}` : undefined }}>
                    <span className="block text-[13px] font-semibold mb-1" style={{ color: c.text }}>{q.label}</span>
                    <textarea rows={1} value={form.questions[q.key] ?? ''} placeholder={q.placeholder}
                      onChange={(e) => { const t = e.target.value; setForm((x) => ({ ...x, questions: { ...x.questions, [q.key]: t } })); }}
                      className="w-full bg-transparent outline-none resize-none text-[15px] leading-relaxed placeholder:opacity-60" style={{ color: c.text }} />
                  </label>
                ))}
              </div>
            </div>
          )}

          <label className="block rounded-2xl p-3.5" style={{ background: c.bgElevated, border: `1px solid ${c.border}` }}>
            <span className="block text-[13px] font-bold mb-1.5" style={{ color: c.text }}>Anything else for the manager?</span>
            <textarea rows={2} value={form.note} placeholder="Optional" onChange={(e) => { const t = e.target.value; setForm((x) => ({ ...x, note: t })); }}
              className="w-full bg-transparent outline-none resize-none text-[15px] leading-relaxed placeholder:opacity-60" style={{ color: c.text }} />
          </label>

          {error && <p className="text-sm" style={{ color: c.danger }}>{error}</p>}

          <div className="sticky bottom-0 -mx-4 sm:mx-0 px-4 sm:px-0 pt-3 pb-4" style={{ background: c.bg }}>
            <div className="flex gap-2">
              {editing && (
                <button onClick={() => { setEditing(false); setForm(parseCheckin(existing?.answers)); setError(null); }}
                  className="h-[52px] px-5 rounded-xl text-sm font-semibold" style={{ background: c.bgElevated, border: `1px solid ${c.border}`, color: c.text }}>Cancel</button>
              )}
              <button onClick={() => void send()} disabled={saving}
                className="flex-1 h-[52px] rounded-xl text-[15px] font-bold flex items-center justify-center gap-2 disabled:opacity-50"
                style={{ background: c.accent, color: '#fff' }}>
                {saving ? <Loader2 size={16} className="animate-spin" /> : null}
                {editing ? 'Save changes' : 'Send check-in'}
              </button>
            </div>
          </div>
        </div>
      )}
    </motion.div>
  );
}
